/* ============================================================
   Edit a task: name, colour (only shown while unfiled — a filed task wears its
   project's), project, tags, day, time of day and repeat. Day changes go
   through `rescheduleTask` (it re-ranks and re-anchors a repeat); everything
   else is one `updateTask` patch.
   ============================================================ */
import React, { useState } from 'react';
import { presetToRRule, type RecurrencePreset, rruleToPreset, type Weekday, WEEKDAY_CODES } from '@habishine/shared';
import { type HueName, type Task, useStore } from '@habishine/client-core';

import { Dialog, inputClass, Label } from './Dialog';
import { HuePicker } from './HuePicker';
import { Button } from './ui';

type Repeat = 'none' | 'daily' | 'weekdays' | 'weekly' | 'monthly' | 'custom';

function weekdayOf(day: string): Weekday {
  const [y = 0, m = 1, d = 1] = day.split('-').map(Number);
  return WEEKDAY_CODES[new Date(y, m - 1, d).getDay()] ?? 'MO';
}

function repeatOf(rrule: string | null | undefined): Repeat {
  if (!rrule) return 'none';
  const p = rruleToPreset(rrule);
  if (!p || p.interval !== 1) return 'custom';
  if (p.freq === 'daily') return 'daily';
  if (p.freq === 'monthly') return 'monthly';
  if (p.byday.length === 5 && !p.byday.includes('SA') && !p.byday.includes('SU')) return 'weekdays';
  return p.byday.length === 1 ? 'weekly' : 'custom';
}

function presetFor(repeat: Repeat, day: string | null): RecurrencePreset | null {
  switch (repeat) {
    case 'daily': return { freq: 'daily', interval: 1 };
    case 'weekdays': return { freq: 'weekly', interval: 1, byday: ['MO', 'TU', 'WE', 'TH', 'FR'] };
    case 'weekly': return { freq: 'weekly', interval: 1, byday: [weekdayOf(day ?? new Date().toISOString().slice(0, 10))] };
    case 'monthly': return { freq: 'monthly', interval: 1 };
    default: return null;
  }
}

const toMinutes = (hhmm: string): number | null => {
  const [h, m] = hhmm.split(':').map(Number);
  return hhmm && Number.isFinite(h) && Number.isFinite(m) ? (h ?? 0) * 60 + (m ?? 0) : null;
};
const toHHMM = (min: number | null | undefined): string =>
  min == null ? '' : `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;

export function TaskEditor({ task, onClose }: { task: Task; onClose: () => void }) {
  const { activeProjects, activeTags, updateTask, rescheduleTask } = useStore();
  const [name, setName] = useState(task.name);
  const [hue, setHue] = useState<HueName>(task.hue);
  const [projectId, setProjectId] = useState<string>(task.projectId ?? '');
  const [tagIds, setTagIds] = useState<string[]>(task.tagIds ?? []);
  const [day, setDay] = useState(task.scheduledFor ?? '');
  const [time, setTime] = useState(toHHMM(task.remindAt));
  const [repeat, setRepeat] = useState<Repeat>(repeatOf(task.rrule));
  const projects = activeProjects();
  const tags = activeTags();

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;
    const newDay = day || null;
    const rrule =
      repeat === 'custom' ? (task.rrule ?? null) : repeat === 'none' ? null : presetToRRule(presetFor(repeat, newDay)!);
    if (newDay !== (task.scheduledFor ?? null)) rescheduleTask(task.id, newDay);
    updateTask(task.id, {
      name: n,
      hue,
      projectId: projectId || null,
      tagIds,
      // a repeat needs a day to anchor on; a time needs a day to resolve to
      rrule: newDay ? rrule : null,
      remindAt: newDay ? toMinutes(time) : null,
    });
    onClose();
  };

  return (
    <Dialog
      title="Edit task"
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" form="task-editor" variant="primary" disabled={!name.trim()}>Save</Button>
        </>
      }
    >
      <form id="task-editor" onSubmit={save} className="space-y-4">
        <label className="block">
          <Label>NAME</Label>
          <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>
        <label className="block">
          <Label>PROJECT</Label>
          <select className={inputClass} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">No project</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        {!projectId ? (
          <div>
            <Label>COLOUR</Label>
            <HuePicker value={hue} onChange={setHue} />
          </div>
        ) : null}
        {tags.length > 0 ? (
          <div>
            <Label>TAGS</Label>
            <div className="flex flex-wrap gap-1">
              {tags.map((t) => {
                const on = tagIds.includes(t.id);
                return (
                  <button key={t.id} type="button" aria-pressed={on}
                    onClick={() => setTagIds((ids) => (on ? ids.filter((x) => x !== t.id) : [...ids, t.id]))}
                    className={`rounded-full px-2.5 py-1 text-xs font-semibold ${on ? 'frog-gradient text-white' : 'bg-card-sunk text-ink-2'}`}>@{t.name}</button>
                );
              })}
            </div>
          </div>
        ) : null}
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <Label>DAY</Label>
            <input type="date" className={inputClass} value={day} onChange={(e) => setDay(e.target.value)} />
          </label>
          <label className="block">
            <Label>TIME</Label>
            <input type="time" className={inputClass} value={time} disabled={!day} onChange={(e) => setTime(e.target.value)} />
          </label>
        </div>
        <label className="block">
          <Label>REPEAT</Label>
          <select className={inputClass} value={repeat} disabled={!day} onChange={(e) => setRepeat(e.target.value as Repeat)}>
            <option value="none">Doesn't repeat</option>
            <option value="daily">Every day</option>
            <option value="weekdays">Weekdays</option>
            <option value="weekly">Weekly, on this day</option>
            <option value="monthly">Monthly</option>
            {repeat === 'custom' ? <option value="custom">Custom (kept as is)</option> : null}
          </select>
          {!day ? <span className="mt-1 block text-xs text-tertiary">Give the task a day to set a time or a repeat.</span> : null}
        </label>
      </form>
    </Dialog>
  );
}
